/* File: src\components\friends\FriendLeaderboard.js */

/* File: src/components/friends/FriendLeaderboard.js */

/* src/components/friends/FriendLeaderboard.js */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useProfile } from '../../contexts/ProfileContext'; // To get active profile ID and friendsList
// *** ADDED: Import useSettings to get timezone ***
import { useSettings } from '../../contexts/SettingsContext';
import { getFunctions, httpsCallable } from 'firebase/functions'; // Import Firebase Functions tools
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'; // Import FontAwesome
// Use Chevron icons consistent with AllSessionsTable
import { faSpinner, faArrowsRotate, faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';

// --- Local Storage Key for Persistence ---
const LOCAL_STORAGE_LEADERBOARD_PERIOD_KEY = 'friendLeaderboardLastPeriod';

// --- Firebase Functions Setup ---
let getFriendLeaderboardFn = null;
try {
    const functions = getFunctions();
    if (functions) {
        getFriendLeaderboardFn = httpsCallable(functions, 'getFriendLeaderboard');
    } else {
        console.warn("FriendLeaderboard: Firebase Functions instance not available.");
    }
} catch (e) {
    console.error("FriendLeaderboard: Error getting Firebase Functions instance:", e);
}
// --- End Firebase Functions Setup ---

// --- Reusable UI Components ---
const LoadingSpinner = ({ small = false }) => (
    <div className={`flex justify-center items-center ${small ? 'h-4 w-4' : 'h-8 w-8'}`}>
        <FontAwesomeIcon
            icon={faSpinner}
            className={`text-indigo-500 ${small ? 'text-sm' : 'text-xl'}`}
            spin
        />
    </div>
);

const ErrorDisplay = ({ message }) => (
    <div className="p-4 my-4 text-sm text-red-700 bg-red-100 rounded-lg border border-red-300 text-center" role="alert">
        <span className="font-medium">Error:</span> {message || 'Could not load leaderboard data.'}
    </div>
);

const RefreshIcon = () => (
    <FontAwesomeIcon icon={faArrowsRotate} className="h-4 w-4" />
);
// --- End UI Components ---

// --- Period Definitions ---
const PERIODS = {
    TODAY: { key: 'today', label: 'Today' },
    LAST_7_DAYS: { key: 'last7', label: 'Last 7 Days' },
    LAST_30_DAYS: { key: 'last30', label: 'Last 30 Days' },
    ALL_TIME: { key: 'allTime', label: 'All Time' },
};

// --- Sorting Definitions ---
// Align column keys with backend data and define labels/types
const SORTABLE_COLUMNS = [
    { key: 'rank', label: 'Rank', type: 'number', defaultSort: true, align: 'center' },
    { key: 'username', label: 'Username', type: 'string', align: 'left' },
    { key: 'timePlayed', label: 'Time', type: 'number', align: 'left' },
    { key: 'sessions', label: 'Sessions', type: 'number', align: 'center' },
    { key: 'streak', label: 'Streak 🔥', type: 'number', align: 'center' }, // ADDED STREAK COLUMN
    { key: 'avgLevel', label: 'Avg Lvl', type: 'number', align: 'center' },
    { key: 'avgDprime', label: 'Avg d\'', type: 'number', align: 'center' },
    { key: 'highestLevelPeriod', label: 'Peak Lvl', type: 'number', align: 'center' },
    { key: 'currentLevel', label: 'Curr Lvl', type: 'number', align: 'center' }
];

const DEFAULT_SORT_COLUMN = 'avgLevel'; // Default sort column remains avgLevel
const DEFAULT_SORT_DIRECTION = 'desc'; // Default sort direction
// *** ADDED: Default timezone fallback ***
const DEFAULT_TIMEZONE = 'UTC';
// *** ADDED: Default period if nothing saved ***
const DEFAULT_PERIOD_KEY = PERIODS.TODAY.key; // Default to Today

// --- Helper to format currentLevel (treats '--' as low value) ---
const formatCurrentLevel = (level) => {
    if (level === '--' || level === null || level === undefined) return '--';
    const num = Number(level);
    return isNaN(num) ? '--' : num.toFixed(2);
};

const getSortValue = (item, columnKey, columnType) => {
    const value = item[columnKey];
    if (value === null || value === undefined || value === '--') {
        // Treat nulls/missing/-- as lowest value for sorting
        return columnType === 'string' ? '' : Number.NEGATIVE_INFINITY;
    }
    // For streak, if it's 0, treat it as 0, not negative infinity.
    if (columnKey === 'streak' && Number(value) === 0) {
        return 0;
    }
    return columnType === 'number' ? Number(value) : String(value).toLowerCase();
};

/**
 * FriendLeaderboard Component
 * Fetches and displays ranked friend statistics based on selected time periods.
 * Includes responsive design, sorting, and timezone support.
 * *** Default period is 'Today'. Selected period persists in localStorage. ***
 */
const FriendLeaderboard = () => {
    const { activeProfile } = useProfile(); // Get activeProfile (includes friendsList)
    // *** ADDED: Get settings context for timezone ***
    const { settings } = useSettings();
    const [leaderboardData, setLeaderboardData] = useState([]);
    // *** MODIFIED: Initialize selectedPeriod from localStorage or default to Today ***
    const [selectedPeriod, setSelectedPeriod] = useState(() => {
        // Check if localStorage is available (runs only on component mount)
        if (typeof window !== 'undefined' && window.localStorage) {
            try {
                const savedPeriod = localStorage.getItem(LOCAL_STORAGE_LEADERBOARD_PERIOD_KEY);
                // Validate if the saved period is one of the valid keys
                if (savedPeriod && Object.values(PERIODS).some(p => p.key === savedPeriod)) {
                    // console.log("FriendLeaderboard: Loading period from localStorage:", savedPeriod); // Optional debug log
                    return savedPeriod; // Use saved period
                } else {
                     // console.log("FriendLeaderboard: Saved period invalid or not found, defaulting."); // Optional debug log
                }
            } catch (e) {
                console.error("Error reading leaderboard period from localStorage:", e);
                // Fallback to default if error occurs
            }
        }
        // Default to 'Today' if nothing saved or localStorage unavailable/error
        // console.log("FriendLeaderboard: Defaulting to period:", DEFAULT_PERIOD_KEY); // Optional debug log
        return DEFAULT_PERIOD_KEY;
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // *** ADDED: Get User Timezone using useMemo ***
    const userTimezone = useMemo(() => {
        // Retrieve timezone from settings context, fallback to default
        return settings?.timezone || DEFAULT_TIMEZONE;
    }, [settings?.timezone]); // Dependency on settings.timezone

    // Memoize friend list dependency string (remains the same)
    const friendListDependency = useMemo(() => {
        const friendIds = Array.isArray(activeProfile?.friendsList)
            ? activeProfile.friendsList.map(f => f.id).sort()
            : [];
        return JSON.stringify(friendIds);
    }, [activeProfile?.friendsList]);

    // Fetch function - now includes timezone parameter and passes it
    const fetchLeaderboardData = useCallback(async (periodKey, timezone) => { // Added timezone parameter
        if (!getFriendLeaderboardFn) { setError("Leaderboard feature unavailable."); setIsLoading(false); return; }
        if (!activeProfile?.id) { setLeaderboardData([]); setError(null); setIsLoading(false); return; }

        // console.log(`Fetching leaderboard data for profile ${activeProfile.id}, period: ${periodKey}, timezone: ${timezone}`); // Keep commented
        setIsLoading(true); setError(null);
        try {
            // *** MODIFIED: Pass timezone in the payload to the Cloud Function ***
            const result = await getFriendLeaderboardFn({
                requestingProfileId: activeProfile.id,
                periodKey: periodKey,
                timezone: timezone // Pass the determined user timezone
            });
            // Reminder: Backend 'getFriendLeaderboard' MUST be updated
            // to accept and USE the 'timezone' parameter for filtering.

            if (result?.data?.success && Array.isArray(result.data.leaderboard)) {
                setLeaderboardData(result.data.leaderboard);
                 // Reset sort state when data reloads initially
                setSortColumn(DEFAULT_SORT_COLUMN);
                setSortDirection(DEFAULT_SORT_DIRECTION);
            } else {
                const backendMessage = result?.data?.message || 'Backend returned invalid data.';
                setError(backendMessage); setLeaderboardData([]);
            }
        } catch (err) {
            // Only set error if it's not an "unauthenticated" error during logout/refresh race conditions
            if (err.code !== 'unauthenticated') {
                 setError(err.message || 'Error fetching data.');
            } else {
                 setError(null); // Clear error on expected unauth
            } setLeaderboardData([]);
        } finally { setIsLoading(false); }
    }, [activeProfile?.id]); // Dependency: only profile ID. Timezone is passed directly.

    // Effect to fetch data - now passes userTimezone
    useEffect(() => {
        if (activeProfile?.id) {
            // *** MODIFIED: Pass userTimezone to fetch function ***
            fetchLeaderboardData(selectedPeriod, userTimezone);
        }
        else { setLeaderboardData([]); setError(null); setIsLoading(false); }
        // Re-fetch if period, profile ID, friend list, or timezone changes
    }, [selectedPeriod, activeProfile?.id, friendListDependency, userTimezone, fetchLeaderboardData]); // Added userTimezone dependency

    // *** ADDED: Effect to save the selected period to localStorage ***
    useEffect(() => {
        // Check if localStorage is available
        if (typeof window !== 'undefined' && window.localStorage) {
            try {
                // console.log("FriendLeaderboard: Saving period to localStorage:", selectedPeriod); // Optional debug log
                localStorage.setItem(LOCAL_STORAGE_LEADERBOARD_PERIOD_KEY, selectedPeriod);
            } catch (e) {
                console.error("Error saving leaderboard period to localStorage:", e);
            }
        }
    }, [selectedPeriod]); // Run this effect whenever selectedPeriod changes

    // Manual refresh handler - passes userTimezone
    const handleRefresh = () => {
        if (activeProfile?.id) {
            // *** MODIFIED: Pass userTimezone on refresh ***
             fetchLeaderboardData(selectedPeriod, userTimezone);
        }
    };

    // --- Formatting Helpers ---
    const formatTimePlayed = (ms) => { /* ... (keep existing formatter) ... */  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms <= 0) return '0s'; const totalSeconds = Math.floor(ms / 1000); const hours = Math.floor(totalSeconds / 3600); const minutes = Math.floor((totalSeconds % 3600) / 60); const seconds = totalSeconds % 60; let result = ''; if (hours > 0) result += `${hours}h `; if (minutes > 0 || hours > 0) result += `${minutes}m `; if ((hours === 0 && minutes === 0 && seconds >= 0) || (seconds > 0 && (hours > 0 || minutes > 0))) { result += `${seconds}s`; } return result.trim() || '0s'; };
    const formatLevel = (level) => (level !== null && level !== undefined && Number.isFinite(level) ? level.toFixed(2) : '--');
    const formatDprime = (dprime) => (dprime !== null && dprime !== undefined && Number.isFinite(dprime) ? dprime.toFixed(2) : '--');
    const formatStreak = (streak) => (streak !== null && streak !== undefined && Number.isFinite(streak) ? streak : 0);
    // --- End Formatters ---

    // --- Sorting State and Logic ---
    const [sortColumn, setSortColumn] = useState(DEFAULT_SORT_COLUMN);
    const [sortDirection, setSortDirection] = useState(DEFAULT_SORT_DIRECTION);

    const handleSort = (columnKey) => {
        if (sortColumn === columnKey) {
            // Flip direction if clicking the same column
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            // Set new column and default direction based on type
            const columnConfig = SORTABLE_COLUMNS.find(c => c.key === columnKey);
            setSortColumn(columnKey);
            // Default to ascending for rank/username, descending for numeric stats
            setSortDirection(columnConfig?.type === 'string' || columnConfig?.key === 'rank' ? 'asc' : 'desc');
        }
    };

    const sortedLeaderboard = useMemo(() => {
        if (!leaderboardData || leaderboardData.length === 0) return [];

        const columnConfig = SORTABLE_COLUMNS.find(c => c.key === sortColumn);
        if (!columnConfig) return leaderboardData;

        const sorted = [...leaderboardData].sort((a, b) => {
            const valA = getSortValue(a, columnConfig.key, columnConfig.type);
            const valB = getSortValue(b, columnConfig.key, columnConfig.type);

            if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
            if (valA > valB) return sortDirection === 'asc' ? 1 : -1;

            // Secondary sort: avgLevel desc if primary sort is equal (unless primary is avgLevel)
            if (columnConfig.key !== 'avgLevel') {
                const avgA = getSortValue(a, 'avgLevel', 'number');
                const avgB = getSortValue(b, 'avgLevel', 'number');
                if (avgA !== avgB) return avgB - avgA; // Descending avgLevel
            }
            // Tertiary sort: Time Played desc if still equal
            if (columnConfig.key !== 'timePlayed') {
                 const timeA = getSortValue(a, 'timePlayed', 'number');
                 const timeB = getSortValue(b, 'timePlayed', 'number');
                 if (timeA !== timeB) return timeB - timeA; // Descending timePlayed
            }

            return 0; // Items are equal
        });
        // Recalculate rank based on the new sort order
        return sorted.map((item, index) => ({ ...item, rank: index + 1 }));
    }, [leaderboardData, sortColumn, sortDirection]);

    // Get sort icon helper - adopted from AllSessionsTable
    const getSortIcon = (field) => {
        if (sortColumn !== field) {
            // Use a subtle icon for non-active columns, like AllSessionsTable
            return <FontAwesomeIcon icon={faChevronDown} className="ml-1 text-gray-400 opacity-50 h-3 w-3 inline-block align-middle" />;
        }
        return sortDirection === 'asc'
            ? <FontAwesomeIcon icon={faChevronUp} className="ml-1 text-indigo-500 h-3 w-3 inline-block align-middle" />
            : <FontAwesomeIcon icon={faChevronDown} className="ml-1 text-indigo-500 h-3 w-3 inline-block align-middle" />;
    };

    // --- Render Component JSX ---
    return (
        // Main container with responsive padding and matching background/border
        <div className="p-4 sm:p-6 bg-white rounded-lg border border-gray-200 shadow-sm relative">

            {/* Header: Title and Refresh Button */}
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-700">Friend Leaderboard</h3>
                <button
                    onClick={handleRefresh}
                    disabled={isLoading || !activeProfile?.id}
                    className="p-1.5 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Refresh Leaderboard"
                >
                    {isLoading ? <LoadingSpinner small={true} /> : <RefreshIcon />}
                </button>
            </div>

            {/* Period Selector Buttons - Added flex-wrap */}
            <div className="mb-4 flex flex-wrap gap-2">
                {Object.values(PERIODS).map(period => (
                    <button
                        key={period.key}
                        onClick={() => setSelectedPeriod(period.key)} // This triggers the state update and the save effect
                        disabled={isLoading || !activeProfile?.id}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                            selectedPeriod === period.key
                                ? 'bg-indigo-600 text-white focus:ring-indigo-500 shadow-sm'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-indigo-300 disabled:opacity-50 disabled:cursor-not-allowed'
                        }`}
                    >
                        {period.label}
                    </button>
                ))}
            </div>

            {/* Leaderboard Table/List Area */}
            {/* Wrap table in div with overflow-x-auto for horizontal scrolling */}
            {/* Match AllSessionsTable outer div style */}
            <div className="overflow-x-auto">
                {/* Loading State */}
                {isLoading && sortedLeaderboard.length === 0 ? (
                    <div className="flex justify-center items-center py-8">
                        <LoadingSpinner /> {/* Use standard size spinner */}
                    </div>
                ) : error ? (
                    <ErrorDisplay message={error} />
                ) : !activeProfile?.id ? (
                    <p className="text-center text-sm text-gray-500 py-6 italic">Select profile to view leaderboard.</p>
                ) : sortedLeaderboard.length === 0 && !isLoading ? (
                     // Match AllSessionsTable empty state style
                     <div className="p-8 text-center">
                        <p className="text-gray-500 mb-2">No friend data available for this period.</p>
                        <p className="text-sm text-gray-400">Add some friends or try a different time period.</p>
                    </div>
                ) : (
                    // Render the leaderboard table
                    // Match AllSessionsTable table classes
                    <table className="min-w-full divide-y divide-gray-200">
                        {/* Table Header - Match AllSessionsTable header classes */}
                        <thead className="bg-gray-50">
                            <tr>
                                {/* Generate headers dynamically */}
                                {SORTABLE_COLUMNS.map(col => (
                                    <th
                                        key={col.key}
                                        scope="col"
                                        // Match AllSessionsTable th classes + alignment
                                        className={`px-4 py-3 text-${col.align || 'left'} text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100`}
                                        onClick={() => handleSort(col.key)}
                                        title={`Sort by ${col.label}`} // Improve title
                                    >
                                        {/* Match AllSessionsTable header content structure */}
                                        <div className={`flex items-center ${col.align === 'center' ? 'justify-center' : ''}`}>
                                            {/* Icons could be added here if desired */}
                                            <span>{col.label}</span>
                                            {getSortIcon(col.key)}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        {/* Table Body - Match AllSessionsTable body classes */}
                        <tbody className="bg-white divide-y divide-gray-200">
                            {sortedLeaderboard.map((entry) => {
                                // Check against activeProfile.id
                                const isCurrentUserActiveProfile = entry.profileId === activeProfile?.id;
                                return (
                                    // Row styling: Only apply hover effect
                                    <tr
                                        key={entry.profileId}
                                        className={`hover:bg-gray-50 transition-colors duration-150`}
                                    >
                                        {/* Rank Cell */}
                                        <td className="px-4 py-3.5 whitespace-nowrap text-xs sm:text-sm text-gray-900 text-center">{entry.rank}</td>
                                        {/* Username Cell - Apply conditional styling based on active profile */}
                                        <td className={`px-4 py-3.5 whitespace-nowrap text-xs sm:text-sm text-left truncate ${isCurrentUserActiveProfile ? 'text-indigo-600 font-semibold' : 'text-gray-800'}`} title={entry.username}>{entry.username}</td>
                                        {/* Other Cells - Use default styling */}
                                        <td className="px-4 py-3.5 whitespace-nowrap text-xs sm:text-sm text-gray-500 text-left">{formatTimePlayed(entry.timePlayed)}</td>
                                        <td className="px-4 py-3.5 whitespace-nowrap text-xs sm:text-sm text-gray-500 text-center">{entry.sessions}</td>
                                        <td className="px-4 py-3.5 whitespace-nowrap text-xs sm:text-sm text-gray-500 text-center">{formatStreak(entry.streak)}</td>
                                        <td className="px-4 py-3.5 whitespace-nowrap text-xs sm:text-sm text-gray-500 text-center">{formatLevel(entry.avgLevel)}</td>
                                        <td className="px-4 py-3.5 whitespace-nowrap text-xs sm:text-sm text-gray-500 text-center">{formatDprime(entry.avgDprime)}</td>
                                        <td className="px-4 py-3.5 whitespace-nowrap text-xs sm:text-sm text-gray-500 text-center">{formatLevel(entry.highestLevelPeriod)}</td>
                                        <td className="px-4 py-3.5 whitespace-nowrap text-xs sm:text-sm text-gray-500 text-center">{formatCurrentLevel(entry.currentLevel)}</td>
                                    </tr>
                                );
                           })}
                        </tbody>
                    </table>
                )}
            </div> {/* End overflow-x-auto */}
             {/* Reminder for backend update */}
             {getFriendLeaderboardFn && !error && sortedLeaderboard.length > 0}
        </div> // End main container
    );
};

export default FriendLeaderboard;